import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faEnvelope,
  faUnlockAlt,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faGithub,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  FormCheck,
  Container,
  InputGroup,
  Spinner,
} from "@themesberg/react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BgImage from "../../assets/img/illustrations/signin.svg";
import { BaseUrl } from "../../Routes/BaseUrl";

export default () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validate = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    if (!password) newErrors.password = "Password is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log BaseUrl to verify it's correct
    console.log("=== LOGIN ATTEMPT DEBUG ===");
    console.log("BaseUrl value:", BaseUrl);
    console.log("Full URL:", BaseUrl.trim() + '/user/login');
    console.log("Email:", email);
    console.log("===========================");
    
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    
    try {
      console.log("Attempting login request...");
      
      const response = await axios.post(BaseUrl.trim() + '/user/login',{
        email:email,
        password,
      }, {
        withCredentials: true  // Enable credentials for CORS
      });
      
      // Log successful response for debugging
      console.log("✅ Login response received:", response);
      
      // Handle success
      if (response.status === 200) {
        // Check if token exists before storing
        if (response.data && response.data.token) {
          localStorage.setItem("token", response.data.token);
          console.log("Token stored successfully");
          
          // Display success message if available
          if (response.data.message) {
            toast.success(response.data.message);
          } else {
            toast.success("Login successful!");
          }
          
          // Redirect to dashboard
          window.location.href = "/dashboard";
        } else {
          // Token missing in response
          console.error("No token in response:", response.data);
          toast.error("Login failed: No authentication token received");
        }
      }
    } catch (error) {
      // Enhanced error logging
      console.error("❌ LOGIN ERROR CAUGHT ===");
      console.error("Error type:", error.constructor.name);
      console.error("Error message:", error.message);
      console.error("Full error object:", error);
      
      // Check if it's a URL/network issue before the request is even made
      if (error.message && error.message.includes('Invalid URL')) {
        console.error("⚠️ INVALID URL ERROR - BaseUrl might be undefined or malformed");
        console.error("Current BaseUrl:", BaseUrl);
        console.error("process.env.REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
        toast.error(`Invalid API URL: ${BaseUrl}. Check environment variables.`);
        setLoading(false);
        return;
      }
      
      // Handle backend errors (server responded with error status)
      if (error.response) {
        const status = error.response.status;
        // Always prioritize showing the actual backend error message
        const errorMessage = error.response.data?.message || 
                           error.response.data?.error || 
                           error.response.data?.msg ||
                           `Server error (${status})`;
        
        console.error("Backend error:", {
          status: status,
          data: error.response.data,
          message: errorMessage
        });
        
        // Display the actual backend message instead of generic messages
        toast.error(`Login failed: ${errorMessage}`);
      } 
      // Handle network errors (request made but no response received)
      else if (error.request) {
        console.error("Network error - No response received");
        console.error("Request details:", error.request);
        console.error("This usually means:");
        console.error("1. Backend is not running");
        console.error("2. CORS is blocking the request");
        console.error("3. URL is incorrect:", BaseUrl.trim() + '/user/login');
        toast.error("Login failed: No response from server. Check backend or CORS.");
      } 
      // Handle unexpected errors (error setting up the request)
      else {
        const errorMessage = error.message || "An unexpected error occurred";
        console.error("Unexpected error (request setup failed):", errorMessage);
        console.error("This usually means the request couldn't be created");
        console.error("Check if BaseUrl is valid:", BaseUrl);
        toast.error(`Login failed: ${errorMessage}`);
      }
      
      console.error("=========================");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <Row
            className="justify-content-center form-bg-image"
            style={{ backgroundImage: `url(${BgImage})` }}
          >
            <Col
              xs={12}
              className="d-flex align-items-center justify-content-center"
            >
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h3 className="mb-0">Sign in to Avijo</h3>
                </div>
                <Form className="mt-4" onSubmit={handleSubmit}>
                  <Form.Group id="email" className="mb-4">
                    <Form.Label>Your Email</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faEnvelope} />
                      </InputGroup.Text>
                      <Form.Control
                        autoFocus
                        type="email"
                        placeholder="example@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group id="password" className="mb-4">
                    <Form.Label>Your Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUnlockAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        isInvalid={!!errors.password}
                      />
                      <InputGroup.Text
                        onClick={togglePasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? faEye : faEyeSlash}
                        />
                      </InputGroup.Text>
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Form.Check type="checkbox">
                      <FormCheck.Input id="defaultCheck5" className="me-2" />
                      <FormCheck.Label htmlFor="defaultCheck5" className="mb-0">
                        Remember me
                      </FormCheck.Label>
                    </Form.Check>
                  </div>
                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={loading}
                    onClick={(e) => {
                      console.log("Sign in button clicked!");
                      console.log("Loading state:", loading);
                      console.log("Form will submit...");
                    }}
                  >
                    {loading ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      "Sign in"
                    )}
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};
